import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  BreakBar,
  InfoBox,
  InfoList,
  UpfrontPaymentBigCost,
  UpfrontPaymentButtonRow,
  UpfrontPaymentDesktopContainer,
  UpfrontPaymentFieldRow,
  UpfrontPaymentFieldRowTitle,
} from "./UpfrontPayment.styles";
import { getCentsToDollars, Bill } from "./Services/UpfrontPaymentService";
import { Button } from "../shared/Button/Button";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { OA100PaymentStripe } from "./OA100PaymentStripe";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { PaidIcon } from "../../assets/iconComponents/PaidIcon";
import { OA100Receipt } from "./OA100Receipt";
import { CheckEOBFileUploader } from "./CheckEOBUploader";
import { OA100NoPaymentRequired } from "./OA100NoPaymentRequired";
import UpfrontPaymentPhoneLink from "./UpfrontPaymentPhoneLink";

const stripePromise = import.meta.env.VITE_UPFRONT_STRIPE_PK
  ? loadStripe(import.meta.env.VITE_UPFRONT_STRIPE_PK)
  : null;

export const UpfrontPaymentOA100: React.FC<{
  upfrontBill: Bill;
  setUpfrontBill: React.Dispatch<React.SetStateAction<Bill | undefined>>;
}> = ({ upfrontBill, setUpfrontBill }) => {
  const { t } = useTranslation();
  const { deviceType } = useDeviceHook();
  const [showOA100Stripe, setShowOA100Stripe] = useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);

  if (
    !upfrontBill ||
    upfrontBill.oa100_balance === undefined ||
    upfrontBill.oa100_balance === null
  ) {
    return null;
  }

  const options = React.useMemo(
    () => ({
      mode: "payment" as const,
      paymentMethodCreation: "manual" as const,
      currency: "usd",
      amount: upfrontBill.oa100_balance,
    }),
    [upfrontBill.oa100_balance],
  );

  useEffect(() => {
    if (upfrontBill.oa_100_billing_status === "paid_in_full") {
      setPaymentSubmitted(true);
    }
  }, [upfrontBill]);

  const openOA100Stripe = () => {
    setShowOA100Stripe(true);
  };

  if (upfrontBill.oa_100_billing_status === "no_oa_100_payment_required") {
    return <OA100NoPaymentRequired />;
  }

  return (
    <>
      {upfrontBill.oa100_balance > 0 && (
        <Elements stripe={stripePromise} options={options}>
          <OA100PaymentStripe
            setUpfrontBill={setUpfrontBill}
            setStripeModalOpen={setShowOA100Stripe}
            stripeModalOpen={showOA100Stripe}
            clientSecret={upfrontBill?.oa_100_client_secret}
            paymentAmount={upfrontBill.oa100_balance}
            setPaymentSubmitted={setPaymentSubmitted}
            useNewCard
          />
        </Elements>
      )}
      <UpfrontPaymentDesktopContainer deviceType={deviceType}>
        <UpfrontPaymentFieldRow adjustable>
          <UpfrontPaymentFieldRowTitle>
            {t("upfrontPayment.oa100.additionalResponsibility")}
          </UpfrontPaymentFieldRowTitle>
          {paymentSubmitted && (
            <>
              <br />
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.multipleBills.paymentStatus")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: <PaidIcon />,
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
              <OA100Receipt />
            </>
          )}
          {!paymentSubmitted && (
            <>
              <UpfrontPaymentBigCost marginTop={8}>
                {getCentsToDollars(upfrontBill.oa100_balance)}
              </UpfrontPaymentBigCost>
              <InfoBox>
                <InfoList>
                  <li>{t("upfrontPayment.oa100.receivedCheck")}</li>
                  <br />
                  <li>
                    {t("upfrontPayment.oa100.checkQuestionsPrefix")}
                    <UpfrontPaymentPhoneLink />
                    {t("upfrontPayment.oa100.questionsSuffix")}
                  </li>
                </InfoList>
              </InfoBox>
            </>
          )}
        </UpfrontPaymentFieldRow>
        {!paymentSubmitted && (
          <UpfrontPaymentButtonRow>
            <Button
              type="submit"
              label={`Pay ${getCentsToDollars(upfrontBill.oa100_balance)}`}
              size="medium"
              onClick={() => openOA100Stripe()}
              backgroundColor="greenDark"
            />
          </UpfrontPaymentButtonRow>
        )}
        <BreakBar />
        <UpfrontPaymentFieldRowTitle>
          Upload EOB or Check
        </UpfrontPaymentFieldRowTitle>
        <CheckEOBFileUploader />
      </UpfrontPaymentDesktopContainer>
    </>
  );
};
